import React, { useState, useEffect } from "react";
import { useNavigate, useParams, useLocation, Link } from "react-router-dom";
import { useDispatch } from "react-redux";
import Swal from "sweetalert2";
import MainLayout from "../../../../components/layout/MainLayout";
import Breadcrumbs from "../../../../components/common/Breadcrumbs";
import SafetyPlanForm from "./SafetyPlanForm";
import { fetchSafetyPlan, updateSafetyPlan } from "../../../../store/slices/safetyPlansSlice";

const EditSafetyPlan = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { id } = useParams();
    const { state } = useLocation();
    const { caseId, patientId } = state || {};

    const [formData, setFormData] = useState({
        warningSigns: "",
        internalCoping: "",
        socialDistractions: [],
        peopleHelp: "",
        professionals: "",
        safeEnvironment: "",
        reasonLiving: "",
    });

    const camelCaseKeys = (obj) => {
        if (Array.isArray(obj)) return obj.map(camelCaseKeys);
        if (obj !== null && typeof obj === "object") {
            return Object.fromEntries(
                Object.entries(obj).map(([k, v]) => [
                    k.replace(/_([a-z])/g, (_, c) => c.toUpperCase()),
                    camelCaseKeys(v),
                ])
            );
        }
        return obj;
    };

    const snakeCaseKeys = (obj) => {
        if (Array.isArray(obj)) return obj.map(snakeCaseKeys);
        if (obj !== null && typeof obj === "object") {
            return Object.fromEntries(
                Object.entries(obj).map(([k, v]) => [
                    k.replace(/([A-Z])/g, "_$1").toLowerCase(),
                    snakeCaseKeys(v),
                ])
            );
        }
        return obj;
    };

    // Fetch existing safety plan
    useEffect(() => {
        const loadData = async () => {
            try {
                const response = await dispatch(fetchSafetyPlan(id)).unwrap();
                // Convert snake_case to camelCase for frontend
                const data = camelCaseKeys(response);
                setFormData({
                    warningSigns: data.warningSigns || "",
                    internalCoping: data.internalCoping || "",
                    socialDistractions: data.socialDistractions || [],
                    peopleHelp: data.peopleHelp || "",
                    professionals: data.professionals || "",
                    safeEnvironment: data.safeEnvironment || "",
                    reasonLiving: data.reasonLiving || "",
                });
            } catch (err) {
                Swal.fire("Error!", "Failed to load Safety Plan", "error");
            }
        };
        loadData();
    }, [id, dispatch]);

    const handleChange = (e, arrayName, index, field) => {
        if (arrayName) {
            const newArray = [...formData[arrayName]];
            newArray[index][field] = e.target.value;
            setFormData({ ...formData, [arrayName]: newArray });
        } else {
            const { name, value } = e.target;
            setFormData({ ...formData, [name]: value });
        }
    };

    const addWarningSign = (arrayName) => {
        setFormData({
            ...formData,
            [arrayName]: [...(formData[arrayName] || []), { name: "", contact: "" }],
        });
    };

    const removeWarningSign = (arrayName, index) => {
        setFormData({
            ...formData,
            [arrayName]: formData[arrayName].filter((_, i) => i !== index),
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const payload = snakeCaseKeys({
                warning_signs: formData.warningSigns,
                internal_coping: formData.internalCoping,
                social_distractions: formData.socialDistractions.map((sd) => ({
                    name: sd.name,
                    contact: sd.contact,
                })),
                people_help: formData.peopleHelp,
                professionals: formData.professionals,
                safe_environment: formData.safeEnvironment,
                reason_living: formData.reasonLiving,
            });

            await dispatch(updateSafetyPlan({ case_id: caseId, planData: payload })).unwrap();

            Swal.fire({
                title: "Updated!",
                text: "Safety Plan updated successfully.",
                icon: "success",
                timer: 2000,
                showConfirmButton: false,
            });

            // Stay on edit page
        } catch (err) {
            Swal.fire("Error!", err || "Failed to update Safety Plan", "error");
        }
    };

    return (
        <MainLayout>
            <div className="container-xxl flex-grow-1 container-p-y">
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <Breadcrumbs />
                    <Link
                        to={`/optional/safety-plan/print/${id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-secondary"
                    >
                        <i className="ri-printer-line me-1"></i> Print
                    </Link>
                </div>
                <div className="card">
                    <div className="card-body">
                        <form onSubmit={handleSubmit}>
                            <SafetyPlanForm
                                formData={formData}
                                handleChange={handleChange}
                                addWarningSign={addWarningSign}
                                removeWarningSign={removeWarningSign}
                            />
                            <div className="col-12 d-flex justify-content-end mt-3">
                                <div className="col-md-2 d-flex justify-content-end gap-2">
                                    <button type="submit" className="btn btn-primary px-4 sticky-form-submit">
                                        <i className="ri-edit-2-line me-1"></i> Update
                                    </button>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </MainLayout>
    );
};

export default EditSafetyPlan;
